import styled from '@emotion/styled';
import { useEffect, useRef, useState } from 'react';

interface MarqueeProps {
  text: string;
  speed?: number;
  className?: string;
}

const MarqueeContainer = styled.div`
  overflow: hidden;
  position: relative;
  width: 100%;
`;

const MarqueeContent = styled.div<{
  $shouldAnimate: boolean;
  $duration: number;
}>`
  display: inline-block;
  white-space: nowrap;
  animation: ${({ $shouldAnimate }) => ($shouldAnimate ? 'marquee' : 'none')}
    ${({ $duration }) => $duration}s linear infinite;

  @keyframes marquee {
    0% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(-50%);
    }
  }
`;

const MarqueeText = styled.span`
  display: inline-block;
  padding-right: 50px;
`;

const Marquee = ({ text, speed = 30, className }: MarqueeProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [duration, setDuration] = useState(10);

  useEffect(() => {
    const checkOverflow = () => {
      if (containerRef.current && contentRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const contentWidth = contentRef.current.scrollWidth / 2; // Divided by 2 because we duplicate the content

        if (contentWidth > containerWidth) {
          setShouldAnimate(true);
          // Calculate duration based on text length and speed
          const calculatedDuration = contentWidth / speed;
          setDuration(Math.max(calculatedDuration, 5)); // Minimum 5 seconds
        } else {
          setShouldAnimate(false);
        }
      }
    };

    checkOverflow();

    // Recheck on resize
    const resizeObserver = new ResizeObserver(checkOverflow);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [text, speed]);

  return (
    <MarqueeContainer ref={containerRef} className={className}>
      <MarqueeContent
        ref={contentRef}
        $shouldAnimate={shouldAnimate}
        $duration={duration}
      >
        <MarqueeText>{text}</MarqueeText>
        {shouldAnimate && <MarqueeText>{text}</MarqueeText>}
      </MarqueeContent>
    </MarqueeContainer>
  );
};

export default Marquee;
